/* eslint-disable no-unreachable */
import React, { useEffect, useState } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router";
import { get, pick, map, keys, find } from "lodash";
import { Formik } from "formik";
import { TextField } from "@mui/material";
import Select from "react-select";

import {
  createKeyValuePair,
  updateKeyValuePair,
  getKeyValuePair,
} from "../../apis/admin";
import { CreateSecretSchema } from "../../globals/validations";
import {
  POONAWALLA_DIALOG_TYPES,
  POONAWALLA_USER_ROLES_TYPES,
  PAGE_MODES,
  ROUTE_DASHBOARD,
  ROUTE_SIGNIN,
  POONAWALLA_SECRETS_ENVIRONMENTS_TYPES,
} from "../../globals/constants";
import { BaseInputStyles } from "../../globals/styles/select";
import { showErrorToaster, showSuccessToaster } from "../../globals/toaster";
import { RefreshDataActions } from "../../redux/reducers/RefreshDataReducer";
import { verifyToken } from "../../globals/utils/jwtUtils";

import PoonaWallaModal from "../PoonaWallaModal";
import PoonaWallaBasicLoader from "../PoonaWallaBasicLoader";

const SecretModal = () => {
  const { authData } = useSelector(
    (store) => ({
      authData: store.auth.data || {},
    }),
    shallowEqual
  );

  const dispatch = useDispatch();
  const location = useLocation();
  const history = useHistory();
  const qp = new URLSearchParams(location.search);

  const initialFormValues = {
    key: "",
    value: "",
    description: "",
    environment: "",
    status: "ACTIVE",
  };

  const [pageMode, setPageMode] = useState(undefined);
  const [isLoading, setIsLoading] = useState(false);
  const [secretData, setSecretData] = useState(initialFormValues);

  const handleUnmount = () => {
    // Remove Qparam & replace
    qp.delete("secretmanagermodal");
    history.replace({
      search: qp.toString(),
    });
  };

  const handleSecretConfig = async (
    values,
    { setSubmitting, setFieldError, setStatus }
  ) => {
    try {
      setSubmitting(true);

      if (pageMode === PAGE_MODES.CREATE) {
        await createKeyValuePair(values);
        showSuccessToaster("Secret Created.");
      } else {
        await updateKeyValuePair(qp.get("secretmanagermodal"), values);
        showSuccessToaster("Secret Updated.");
      }

      handleUnmount();
      dispatch(RefreshDataActions.SET_TO_REFRESH());
    } catch (error) {
      const data = get(error, "data");
      if (get(data, "type") === POONAWALLA_DIALOG_TYPES.FIELD) {
        map(keys(get(data, "detail")), (key) =>
          setFieldError(key, get(data, ["detail", key], ""))
        );
      } else {
        showErrorToaster(get(error, "data.detail", "Something went wrong."));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleGetSecrets = async (id) => {
    try {
      setIsLoading(true);
      const { data } = await getKeyValuePair(id);
      setSecretData(
        pick(data, ["key", "value", "description", "status", "environment"])
      );
    } catch (error) {
      showErrorToaster(get(error, "data.detail", "Something went wrong."));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (qp.get("secretmanagermodal")) {
      // IF NOT LOGGED IN
      if (!get(authData, "idToken") || !verifyToken(get(authData, "idToken"))) {
        history.push(ROUTE_SIGNIN);
      } else if (
        // IF LOGGED IN BUT NOT AN ADMIN, THEN THROW HIM INTO DASHBOARD
        ![
          POONAWALLA_USER_ROLES_TYPES.types.ADMIN,
          POONAWALLA_USER_ROLES_TYPES.types.MAKER,
          POONAWALLA_USER_ROLES_TYPES.types.CHECKER,
        ].includes(get(authData, "user_type"))
      ) {
        history.push(ROUTE_DASHBOARD);
      } else {
        // CREATE APPLICATION MODE
        if (qp.get("secretmanagermodal") === "new") {
          setPageMode(PAGE_MODES.CREATE);
        } else {
          // UPDATE APPLICATION MODE
          setPageMode(PAGE_MODES.EDIT);
          handleGetSecrets(qp.get("secretmanagermodal"));
        }
      }
    } else {
      setPageMode(undefined);
      setSecretData(initialFormValues);
    }
  }, [location.search]);

  return (
    <>
      {pageMode && (
        <PoonaWallaModal
          size="lg"
          handleHideModal={handleUnmount}
          isActive={Boolean(pageMode)}
          title={
            pageMode === PAGE_MODES.CREATE ? "Create Secret" : "Update Secret"
          }
        >
          {isLoading ? (
            <PoonaWallaBasicLoader isLoading={isLoading} />
          ) : (
            <div className="CreateSecretModal">
              <Formik
                initialValues={secretData}
                validationSchema={CreateSecretSchema()}
                onSubmit={handleSecretConfig}
              >
                {({
                  values,
                  errors,
                  touched,
                  setFieldValue,
                  handleBlur,
                  handleSubmit,
                  isSubmitting,
                }) => (
                  <>
                    <form onSubmit={handleSubmit}>
                      <div>
                        <span className="font-weight-bold text-secondary d-block mb-2">
                          Key
                        </span>
                        <TextField
                          name="key"
                          variant="standard"
                          placeholder="Key"
                          className="w-100"
                          value={get(values, "key")}
                          defaultValue={get(values, "key")}
                          error={errors.key && touched.key}
                          helperText={
                            errors.key && touched.key ? errors.key : ""
                          }
                          multiline
                          maxRows={4}
                          onChange={(e) => setFieldValue("key", e.target.value)}
                          onBlur={handleBlur}
                        />
                      </div>

                      <div className="mt-4">
                        <span className="font-weight-bold text-secondary d-block mb-2">
                          Value
                        </span>
                        <TextField
                          name="value"
                          variant="standard"
                          placeholder="Value"
                          className="w-100"
                          value={get(values, "value")}
                          defaultValue={get(values, "value")}
                          error={errors.value && touched.value}
                          helperText={
                            errors.value && touched.value ? errors.value : ""
                          }
                          multiline
                          maxRows={4}
                          onChange={(e) =>
                            setFieldValue("value", e.target.value)
                          }
                          onBlur={handleBlur}
                        />
                      </div>

                      <div className="mt-4">
                        <span className="font-weight-bold text-secondary d-block mb-2">
                          Description
                        </span>
                        <TextField
                          name="description"
                          variant="standard"
                          placeholder="Description"
                          className="w-100"
                          value={get(values, "description")}
                          defaultValue={get(values, "description")}
                          error={errors.description && touched.description}
                          helperText={
                            errors.description && touched.description
                              ? errors.description
                              : ""
                          }
                          multiline
                          maxRows={4}
                          onChange={(e) =>
                            setFieldValue("description", e.target.value)
                          }
                          onBlur={handleBlur}
                        />
                      </div>

                      <div className="mt-4">
                        <span className="font-weight-bold text-secondary d-block mb-2">
                          Environment
                        </span>
                        <Select
                          classNamePrefix="select"
                          placeholder="Select Environment"
                          name="environment"
                          isClearable={false}
                          options={POONAWALLA_SECRETS_ENVIRONMENTS_TYPES}
                          handleBlur={handleBlur}
                          styles={BaseInputStyles(null, "", true, "14px")}
                          onChange={(e) =>
                            setFieldValue("environment", e.value)
                          }
                          value={
                            find(
                              POONAWALLA_SECRETS_ENVIRONMENTS_TYPES,
                              (item) =>
                                item.value === get(values, "environment")
                            ) || null
                          }
                        />
                        {errors.environment && touched.environment && (
                          <span className="error-label fs-0-8 d-block mt-2">
                            {errors.environment}
                          </span>
                        )}
                      </div>

                      <div className="mt-5">
                        <button
                          type="button"
                          onClick={handleUnmount}
                          className="MainButton MainButton__primary mr-3"
                          disabled={isSubmitting}
                        >
                          Cancel
                        </button>
                        <button
                          type="submit"
                          className={`MainButton MainButton__primary outlined mr-3 ${
                            isSubmitting && "loading"
                          }`}
                          disabled={isSubmitting}
                        >
                          {pageMode === PAGE_MODES.CREATE ? "Create" : "Update"}
                        </button>
                      </div>
                    </form>
                  </>
                )}
              </Formik>
            </div>
          )}
        </PoonaWallaModal>
      )}
    </>
  );
};

export default SecretModal;
